import { deflate, inflate } from 'pako';
import type { Conversations } from '@/components/Chat/types';
import type { BedrockMessageContent } from '@/utils/beemAgentTypes';

// How an Insights conversation's messages are stored on its InsightConversation row: JSON,
// deflated and base64 (the format decompressIfNeededThenJsonParse reads elsewhere; plain JSON is
// read too). Kept free of the GraphQL client so it can be tested on its own.

// A DynamoDB item holds 400KB; the other attributes are small. Above this, older tool results'
// rows are trimmed until the conversation fits (the answers and the SQL are always kept).
const MAX_ENCODED = 340 * 1024;
const TRIMMED_ROWS = 20;

// btoa/atob in the browser; Buffer where they do not exist (the unit tests' Node).
const fromBase64 = (value: string): Uint8Array =>
  typeof atob === 'function'
    ? Uint8Array.from(atob(value), (ch) => ch.charCodeAt(0))
    : new Uint8Array(Buffer.from(value, 'base64'));

const toBase64 = (bytes: Uint8Array): string => {
  if (typeof btoa !== 'function') return Buffer.from(bytes).toString('base64');
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
};

export const encodeMessages = (messages: Conversations): string =>
  toBase64(deflate(JSON.stringify(messages)));

export const decodeMessages = (value: string | null | undefined): Conversations => {
  if (!value) return [];
  try {
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch {
      parsed = JSON.parse(inflate(fromBase64(value), { to: 'string' }));
    }
    return Array.isArray(parsed) ? (parsed as Conversations) : [];
  } catch {
    return [];
  }
};

// Tool results carry query rows as a JSON string; a long conversation over wide tables is what
// can outgrow an item. Keep the newest results whole and shorten the older ones, then all of them.
const trimRows = (messages: Conversations, keepNewest: number, rows: number): Conversations => {
  let seen = 0;
  const total = messages.reduce(
    (n, m) => n + (m.content || []).filter((c: BedrockMessageContent) => c?.toolResult).length,
    0,
  );
  return messages.map((message) => ({
    ...message,
    content: (message.content || []).map((item: BedrockMessageContent) => {
      if (!item?.toolResult) return item;
      seen += 1;
      if (total - seen < keepNewest) return item;
      const blocks = (item.toolResult.content || []).map((block: { json?: { rows?: unknown } }) => {
        const raw = block?.json?.rows;
        if (typeof raw !== 'string') return block;
        try {
          const parsed = JSON.parse(raw);
          if (!Array.isArray(parsed) || parsed.length <= rows) return block;
          return {
            ...block,
            json: { ...block.json, rows: JSON.stringify(parsed.slice(0, rows)), truncated: true },
          };
        } catch {
          return block;
        }
      });
      return { ...item, toolResult: { ...item.toolResult, content: blocks } };
    }),
  }));
};

/** The messages as stored: deflated and base64, trimmed of old result rows only if needed. */
export const fitMessages = (messages: Conversations): string => {
  let encoded = encodeMessages(messages);
  if (encoded.length <= MAX_ENCODED) return encoded;
  for (const [keep, rows] of [
    [2, TRIMMED_ROWS],
    [0, TRIMMED_ROWS],
    [0, 0],
  ] as const) {
    encoded = encodeMessages(trimRows(messages, keep, rows));
    if (encoded.length <= MAX_ENCODED) return encoded;
  }
  throw new Error('This conversation is too long to save. Start a new one to keep going.');
};
